import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient, IRequestOptions } from '../../components/shared/http/application-http-client';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LobMail } from '../../models/entities/lob-mail';
import { LobMailPreviewRecipient } from '../../models/entities/lob-mail-recipient';
import { PreviewLobMailRequest } from '../../components/app-layout/mail/mail-sender/preview-mail/preview-mail.component';
import { LobMailSendRequest } from './lob-mail-send-request';
import { LobMailOptOut } from '../../models/entities/lob-mail-opt-out';
import { LobCancelRequest } from '../../components/app-layout/mail/lob-cancel-dialog/lob-cancel-dialog.component';

/**
 * Current mapping of supported Mail Types through Lob
 */
export type LobMailTypes = 'BillPay' | 'Invoice' | 'PDF' | 'Violation';

@Injectable({
    providedIn: 'root',
})
export class LobService
{
    private _http: ApplicationHttpClient;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /** Consolidate getting lob mail for org by switching supported types and batch flag */
    public getOrgMailByType(type: LobMailTypes, isBatch = false): Observable<LobMail[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string;
        switch (type) {
            case 'BillPay':
                // getOrgBillPayMailFlat
                // getOrgBillPayMailBatch not supported
                url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.LobOrgBillPayMail(jwt.orgId));
                break;
            case 'Invoice':
                // getOrgInvoiceMailBatch
                // getOrgInvoiceMailFlat
                url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.LobOrgInvoiceMail(jwt.orgId));
                break;
            case 'PDF':
                // getOrgPDFMailBatched
                // getOrgPDFMailFlat
                url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.LobOrgPDFMail(jwt.orgId));
                break;
            case 'Violation':
                // getOrgViolationMail(Flat)
                // getOrgViolationMailBatch not supported
                url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.LobOrgViolationMail(jwt.orgId));
                break;
        }

        url += (isBatch ? '/batched' : '/flat');

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((mail) => new LobMail(mail));
        }));
    }

    /** Go try to cancel a lob batch or single item */
    public cancelLobMail(request: LobCancelRequest): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Lob(
                        jwt.orgId,
                ) + `/cancel/${request.isBatch ? 'batch' : 'single'}`,
        );

        return this._http.post(url, JSON.stringify(request));
    }

    public patchMailOptOutPreference(isEnrolling: boolean): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.LobUserMailPref(
                        jwt.orgId, jwt.id,
                ),
        );

        return this._http.patch(url, JSON.stringify({isEnrolling: isEnrolling}));
    }


    public getMailOptOutPreference(userId = null): Observable<LobMailOptOut[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.LobUserMailPref(
                        jwt.orgId, userId || jwt.id,
                ),
        );

        return this._http.get(url).pipe(
                map((response: Object[]) => {
                    return response.map(pref => {
                        return new LobMailOptOut(pref);
                    });
                }));
    }

    /**
     * Get tracking events of a bill pay
     *
     * @param paymentId
     */
    public getBillPayTrackingDetail(paymentId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.LobOrgBillPayMail(
                                jwt.orgId,
                        ) + '/tracking/' + paymentId,
                ),
        );
    }

    /**
     * Get responses from a comm activity id
     *
     * @param commActivityId
     */
    public getMailActivityResponses(commActivityId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.Lob(
                                jwt.orgId,
                        ) + '/responses/' + commActivityId,
                ),
        );
    }

    /**
     * Get details of a batch of letters
     *
     * @param organizationId
     * @param letterGroupId
     */
    public getPDFMailBatchDetail(organizationId: number, letterGroupId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.LobOrgPDFMail(organizationId || jwt.orgId) + '/batch/' + letterGroupId,
        );

        return this._http.get(url).pipe(
                map((response: Object[]) => {
                    return response.map(mail => {
                        return new LobMail(mail);
                    });
                }));
    }

    /**
     * Get the comms activity and responses for an on demand sent group
     *
     * @param stmtId
     */
    public getInvoiceMailBatchDetail(stmtId: number): Observable<any[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.get(Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.LobOrgInvoiceMail(jwt.orgId) + '/batch/' + stmtId,
        )).pipe(
                map((response: Object[]) => {
                    return response.map(mail => {
                        return new LobMail(mail);
                    });
                }));
    }

    /**
     * They clicked to preview just one statement from the Lob Mail sender workflow.
     *
     * @param pdf
     * @param options
     */
    public previewLobMailer(
            pdf,
            options: {
                invoice: boolean,
                unitIds: number[],
                ownerId: number,
                includeFuture: boolean,
                includeFutureOnly: boolean,
                includeUploadedPdfForEachUnit: boolean,
            },
    ): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Lob(jwt.orgId) + '/preview',
        );

        const requestOptions: IRequestOptions = {
            responseType: 'blob',
        };

        return this._http.post(url, JSON.stringify({
            invoice: options.invoice,
            unitIds: options.unitIds,
            ownerId: options.ownerId,
            doIncludeFuture: options.includeFuture,
            doIncludeFutureOnly: options.includeFutureOnly,
            includeUploadedPdfForEachUnit: options.includeUploadedPdfForEachUnit,
            pdf: this._arrayBufferToBase64(pdf),
        }), requestOptions);
    }

    _arrayBufferToBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    // LobMailPreviewRecipient[]
    public getMailingAddressesForMailer(request: PreviewLobMailRequest): Observable<LobMailPreviewRecipient[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Lob(jwt.orgId)) + '/addresses',
                JSON.stringify(request),
        ).pipe(map(
                (response: Object[]) => {
                    return response.map(
                            (owner) => {
                                return new LobMailPreviewRecipient(owner);
                            },
                    );
                },
        ));
    }

    /**
     * Send on demand paper statement(s) to selected member(s)
     * @param request
     * @returns {Observable<Object>}
     */
    public sendMailedInvoices(request: LobMailSendRequest): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.LobOrgInvoiceMail(jwt.orgId));

        return this._http.post(url, JSON.stringify(request));
    }

    /**
     * Send mailed violation report
     * @param request
     * @param organizationId
     * @returns {Observable<Object>}
     */
    public sendMailedViolations(
            request: LobMailSendRequest,
            organizationId?: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const orgId = organizationId || jwt.orgId;
        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.LobOrgViolationMail(orgId));

        return this._http.post(url, JSON.stringify(request));
    }

}
